import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { GroupAdminApi } from '@shared/apis';
import { ResponseData } from '@shared/types/http';
import { Profile } from '@shared/types/profile';
import { Package } from '@shared/types/package';
import { Publication } from '@shared/types/publication';

export default class GroupAdminStore extends BaseStore {
    users: Profile[] = [];
    totalUsers: number = 0;

    packages: Package[] = [];
    totalPackages: number = 0;

    userDetail: Profile | null = null;
    userPublications: Publication[] = [];
    totalUserPublications: number = 0;

    userSubscriptions: any[] = [];
    totalUserSubscriptions: number = 0;

    packageItems: any[] = [];
    totalPackageItems: number = 0;

    loading: boolean = false;
    api: GroupAdminApi;

    constructor(rootStore: RootStore) {
        super(rootStore);
        this.api = new GroupAdminApi();
        makeObservable(this, {
            users: observable,
            totalUsers: observable,
            packages: observable,
            totalPackages: observable,
            loading: observable,
            userDetail: observable,
            userPublications: observable,
            totalUserPublications: observable,
            userSubscriptions: observable,
            totalUserSubscriptions: observable,
            fetchUsers: flow.bound,
            fetchPackages: flow.bound,
            fetchUserDetail: flow.bound,
            fetchUserPublications: flow.bound,
            fetchUserSubscriptions: flow.bound,
            fetchPackageItems: flow.bound
        });
    }

    *fetchUsers(params?: any) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getUsers,
                params
            );
            if (res?.ok) {
                this.users = res.data?.elements || [];
                this.totalUsers = res.data?.pagination?.totalRecord || 0;
            }
        } finally {
            this.loading = false;
        }
    }

    *fetchPackages(params?: any) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getPackages,
                params
            );
            if (res?.ok) {
                this.packages = res.data?.elements || [];
                this.totalPackages = res.data?.pagination?.totalRecord || 0;
            }
        } finally {
            this.loading = false;
        }
    }

    *fetchUserDetail(id: string) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getUserDetail,
                id,
                {
                    handleErrorBySelf: true
                }
            );
            if (res?.ok) {
                this.userDetail = res.data;
            }
        } catch (error) {
            console.error('fetchUserDetail error', error);
        } finally {
            this.loading = false;
        }
    }

    *fetchUserPublications(id: string, params?: any) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                () => this.api.getUserPublications(id, params),
                {},
                {
                    handleErrorBySelf: true
                }
            );
            if (res?.ok) {
                this.userPublications = res.data?.elements || res.data?.content || [];
                this.totalUserPublications = res.data?.pagination?.totalRecord || res.data?.paginate?.totalRecord || res.data?.totalElements || res.data?.pagination?.totalElements || 0;
            }
        } catch (error) {
            console.error('fetchUserPublications error', error);
        } finally {
            this.loading = false;
        }
    }

    *fetchUserSubscriptions(params?: any) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                this.api.getUserSubscriptions,
                params
            );
            if (res?.ok) {
                this.userSubscriptions = res.data?.elements || [];
                this.totalUserSubscriptions = res.data?.pagination?.totalRecord || 0;
            }
        } finally {
            this.loading = false;
        }
    }

    *fetchPackageItems(packageId: string, params?: any) {
        this.loading = true;
        try {
            const res: ResponseData = yield this.rootStore.apiStore.call(
                this.api,
                () => this.api.getPackageItems(packageId, params),
                params
            );
            if (res?.ok) {
                this.packageItems = res.data?.elements || [];
                this.totalPackageItems = res.data?.pagination?.totalRecord || 0;
            }
        } finally {
            this.loading = false;
        }
    }
}
